import ChevronDownIcon from "@heroicons/react/24/outline/ChevronDownIcon";
import { useFormContext } from "react-hook-form";
import { twMerge } from "tailwind-merge";

import DatePicker from "~/common/components/ui/DatePicker";
import {
  FieldsetCard,
  getDisabledClasses,
} from "~/common/components/ui/FieldsetCard";
import Input from "~/common/components/ui/Input";
import Select from "~/common/components/ui/select/Select";
import { NOT_AVAILABLE } from "~/common/constants";
import { getTodayDate } from "~/common/utils";
import { PRONOUNS } from "~/patients/constants";
import { useFormatHook } from "~/patients/hooks";
import {
  AccountSchemaType,
  PatientInformation,
  pronounsOptionsWithOther,
} from "~/patients/utils";

interface PersonalDetailsProps {
  currentPatient: PatientInformation;
  disabled?: boolean;
}

export const PersonalDetailsFieldset = ({
  currentPatient,
  disabled,
}: PersonalDetailsProps) => {
  const { transformedPatientData } = useFormatHook();

  const {
    register,
    control,
    formState: { errors },
    watch,
  } = useFormContext<AccountSchemaType>();

  const selectedPronoun = watch("pronouns.pronouns");
  const pronouns = pronounsOptionsWithOther;
  const today = getTodayDate();

  const customPronouns =
    transformedPatientData(currentPatient).pronouns?.customPronouns;

  return (
    <FieldsetCard
      title="Personal Details"
      className="border-none p-0 shadow-none"
      disabled={disabled}
      contentClassName={twMerge(
        "grid-cols-2 sm:grid-cols-4",
        !disabled && "sm:grid-cols-2",
      )}
    >
      <Input
        id="name"
        label="First name"
        {...register("name")}
        {...getDisabledClasses(disabled)}
        error={errors?.name?.message}
      />
      <Input
        id="name"
        label="Last name"
        {...register("last_name")}
        {...getDisabledClasses(disabled)}
        error={errors?.last_name?.message}
      />
      <Select
        error={errors?.pronouns?.message}
        label="Pronouns"
        placeholder={disabled ? NOT_AVAILABLE : "Select pronouns"}
        id="pronouns.pronouns"
        options={pronouns}
        control={control}
        isDisabled={disabled}
        buttonClassName="disabled:bg-transparent disabled:border-none disabled:p-0 disabled:h-auto"
        icon={disabled ? <ChevronDownIcon /> : undefined}
      />
      {selectedPronoun === PRONOUNS.other && (
        <Input
          id="customPronouns"
          label="Custom pronouns"
          placeholder="She/Her/Hers"
          {...register("pronouns.customPronouns")}
          defaultValue={customPronouns}
          error={errors?.pronouns?.customPronouns?.message}
          {...getDisabledClasses(disabled)}
        />
      )}
      <DatePicker
        id="birthday"
        label="Date of birth"
        max={today}
        {...register("birthday")}
        error={errors?.birthday?.message}
        className={twMerge(
          getDisabledClasses(disabled).className,
          "border-gray-300",
        )}
        inputClassName={twMerge(
          getDisabledClasses(disabled).inputClassName,
          "disabled:text-gray-700",
        )}
      />
    </FieldsetCard>
  );
};
